import React, { useState, useEffect, useMemo } from 'react';
import { isModuleAllowedForRole } from '../../shared/rolePermissions';
import {
  User,
  Settings,
  Key,
  Shield,
  Building,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  BadgeCheck,
  Save,
  Search,
  Check,
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Loader2
} from 'lucide-react';

export type ProfileSubTab = 'account_info' | 'system_settings' | 'change_password';

export interface AuthorizedPermissionCategory {
  id: string;
  title: string;
  items: {
    id: string;
    label: string;
    targetModule?: string;
    isRoleScope?: boolean;
  }[];
}

// Complete catalog of authorized modules, roles, and privileges matching the reference UI
export const AUTHORIZED_MODULES_CATALOG: AuthorizedPermissionCategory[] = [
  {
    id: 'sms',
    title: 'Safety Management System',
    items: [
      { id: 'safety_reporting', label: 'Safety Reports', targetModule: 'ingestion' },
      { id: 'safety_investigations', label: 'Safety Investigations', targetModule: 'investigations' },
      { id: 'hazard_register', label: 'Hazard Register', targetModule: 'hazards' },
      { id: 'risk_assessment', label: 'Risk Assessment', targetModule: 'risks' },
      { id: 'mitigation_verification', label: 'Mitigation & Verification', targetModule: 'actions' },
      { id: 'safety_performance', label: 'Safety Performance (SPI/SPT)', targetModule: 'performance' },
      { id: 'bowtie_manager', label: 'Bow Tie Manager', targetModule: 'bowtie' },
      { id: 'rmm_statistic', label: 'RMMStatistic', targetModule: 'analytics' },
      { id: 'risk_mitigation_closure', label: 'Risk+ Mitigation Closure User', isRoleScope: true },
      { id: 'risk_member', label: 'Risk+ Member', isRoleScope: true },
      { id: 'risk_owner', label: 'Risk+ Risk Owner', isRoleScope: true },
      { id: 'risk_responsible', label: 'Risk+ Risk Responsible', isRoleScope: true },
      { id: 'risk_assessment_reader', label: 'Risk+ Assessment Reader', isRoleScope: true },
      { id: 'risk_auditor', label: 'Risk+ Auditor', isRoleScope: true },
      { id: 'risk_entity_admin', label: 'Risk+ Entity Administrator', isRoleScope: true },
      { id: 'risk_assessor', label: 'Risk+ Assessor', isRoleScope: true },
      { id: 'admin_risk_module', label: 'Administrator Risk Management Module', isRoleScope: true },
      { id: 'investigator', label: 'Investigator', isRoleScope: true },
    ],
  },
  {
    id: 'qms',
    title: 'Quality Management',
    items: [
      { id: 'audit_planning_schedule', label: 'Audit Planning & Schedule', targetModule: 'audits' },
      { id: 'audits_inspections', label: 'Audits & Inspections', targetModule: 'inspections' },
      { id: 'findings_non_conformities', label: 'Findings & Non-Conformities', targetModule: 'findings' },
      { id: 'corrective_actions_capa', label: 'Corrective Actions (CAPA)', targetModule: 'capas' },
      { id: 'supplier_vendor_audits', label: 'Supplier & Vendor Audits', targetModule: 'audits' },
      { id: 'audit_reader', label: 'Audit Reader', isRoleScope: true },
      { id: 'qm_entity', label: 'QM Entity', isRoleScope: true },
      { id: 'auditor', label: 'Auditor', isRoleScope: true },
      { id: 'auditee', label: 'Auditee', isRoleScope: true },
      { id: 'evaluator_quality_module', label: 'Evaluator Quality Module', isRoleScope: true },
    ],
  },
  {
    id: 'compliance',
    title: 'Compliance Management',
    items: [
      { id: 'compliance_monitoring', label: 'Compliance Monitoring', targetModule: 'monitoring' },
      { id: 'regulatory_requirements', label: 'Regulatory Requirements', targetModule: 'regulatory' },
      { id: 'regulations_standards', label: 'Regulations & Standards', targetModule: 'regulations' },
      { id: 'exemptions_deviations', label: 'Exemptions & Deviations', targetModule: 'obligations' },
      { id: 'authority_oversight', label: 'Authority Oversight', targetModule: 'matrix' },
      { id: 'compliance_auditor', label: 'Compliance Auditor', isRoleScope: true },
      { id: 'cm_entity_admin', label: 'CM Entity Administrator', isRoleScope: true },
    ],
  },
  {
    id: 'governance_change',
    title: 'Governance & Change',
    items: [
      { id: 'safety_committees_reviews', label: 'Safety Committees & Reviews', targetModule: 'meetings' },
      { id: 'management_of_change', label: 'Management of Change (MoC)', targetModule: 'moc' },
      { id: 'strategic_initiatives', label: 'Strategic Initiatives', targetModule: 'initiatives' },
      { id: 'safety_culture_surveys', label: 'Safety Culture & Surveys', targetModule: 'surveys' },
      { id: 'central_tasks_actions', label: 'Central Tasks & Actions', targetModule: 'tasks_actions' },
      { id: 'moc_approval', label: 'MOC Approval', isRoleScope: true },
      { id: 'project_leader', label: 'Project Leader', isRoleScope: true },
      { id: 'admin_moc_module', label: 'Administrator MOC Module', isRoleScope: true },
    ],
  },
  {
    id: 'emergency_crisis',
    title: 'Emergency & Crisis',
    items: [
      { id: 'emergency_dashboard', label: 'Emergency Dashboard' },
      { id: 'activate_emergency', label: 'Activate Emergency' },
      { id: 'emergency_coordination', label: 'Emergency Coordination' },
      { id: 'emergency_response_plans', label: 'Emergency Response Plans' },
      { id: 'erp_exercises_drills', label: 'ERP Exercises & Drills' },
      { id: 'closed_emergencies', label: 'Closed Emergencies' },
      { id: 'erp_commander', label: 'ERP Commander', isRoleScope: true },
    ],
  },
  {
    id: 'documents_training',
    title: 'Documents & Training',
    items: [
      { id: 'controlled_documents', label: 'Controlled Documents', targetModule: 'documents' },
      { id: 'document_distribution', label: 'Document Distribution', targetModule: 'documents' },
      { id: 'read_acknowledgement', label: 'Read & Acknowledgement', targetModule: 'documents' },
      { id: 'training_competency', label: 'Training & Competency', targetModule: 'training' },
      { id: 'knowledge_base', label: 'Knowledge Base', targetModule: 'documents' },
      { id: 'document_reader', label: 'Document Reader', isRoleScope: true },
      { id: 'document_controller', label: 'Document Controller', isRoleScope: true },
      { id: 'training_competence_evaluator', label: 'Training & Competence Evaluator', isRoleScope: true },
    ],
  },
  {
    id: 'intelligence_analytics',
    title: 'Intelligence & Analytics',
    items: [
      { id: 'safety_quality_dashboard', label: 'Safety & Quality Dashboard', targetModule: 'dashboard' },
      { id: 'risk_overview_heatmap', label: 'Risk Overview & Heatmap', targetModule: 'risks' },
      { id: 'reports_regulatory_returns', label: 'Reports & Regulatory Returns', targetModule: 'reports' },
      { id: 'trend_predictive_analytics', label: 'Trend & Predictive Analytics', targetModule: 'analytics' },
      { id: 'automated_workflows', label: 'Automated Workflows', targetModule: 'workflows' },
      { id: 'workflow_admin', label: 'Workflow Administrator', isRoleScope: true },
    ],
  },
  {
    id: 'administration',
    title: 'Administration',
    items: [
      { id: 'org_system_setup', label: 'Organization & System Setup', targetModule: 'administration' },
      { id: 'users_roles_permissions', label: 'Users, Roles & Permissions', targetModule: 'roles' },
      { id: 'workflow_config', label: 'Workflow Configuration', targetModule: 'workflows' },
      { id: 'integration_hub', label: 'Integration Hub', targetModule: 'integration_attempts' },
      { id: 'system_activity_logs', label: 'System Activity Logs', targetModule: 'administration' },
      { id: 'help_support', label: 'Help & Support', targetModule: 'help' },
      { id: 'system_admin', label: 'System Administrator', isRoleScope: true },
    ],
  },
  {
    id: 'group_membership',
    title: 'Group Membership',
    items: [
      { id: 'safety_compliance_monitoring_lmx', label: 'Safety & Compliance Monitoring – LMX' },
      { id: 'all_company_staff', label: 'All Company Staff' },
      { id: 'auditors_group', label: 'Auditors' },
      { id: 'safety_action_group_lmx', label: 'Safety Action Group – LMX' },
    ],
  },
];

interface UserProfilePageProps {
  initialSubTab?: ProfileSubTab;
  userName?: string;
  username?: string;
  userRole?: string;
  userEmail?: string;
  department?: string;
  position?: string;
  staffId?: string;
  station?: string;
  organizationName?: string;
  organizationCode?: string;
  onNavigate: (tab: string, extraParams?: Record<string, string | null>) => void;
}

export const UserProfilePage: React.FC<UserProfilePageProps> = ({
  initialSubTab = 'system_settings',
  userName = 'Erdal Aydeniz',
  username = 'erdal.aydeniz',
  userRole = 'SAFETY_MANAGER',
  userEmail = 'luminixaviation@gmail.com',
  department = 'Flight Safety & Quality Directorate',
  position = 'Safety Manager',
  staffId = 'EMP-2026-084',
  station = 'LTFM / Istanbul Airport',
  organizationName = 'Luminix Aviation Corp.',
  organizationCode = 'XYZ_AIR',
  onNavigate,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<ProfileSubTab>(initialSubTab);
  const [searchQuery, setSearchQuery] = useState('');

  // Sync activeSubTab when initialSubTab prop changes
  useEffect(() => {
    if (initialSubTab) {
      setActiveSubTab(initialSubTab);
    }
  }, [initialSubTab]);

  // Editable account info state
  const [profileData, setProfileData] = useState({
    name: userName,
    email: userEmail,
    department: department,
    position: position,
    phone: '+90 532 555 0192',
    station: station,
    staffId: staffId,
    reportingManager: 'Capt. Levent Karaca (VP Flight Operations)',
    certifications: 'ICAO Doc 9859 Safety Officer, EASA ORO.GEN.200 Auditor, BowTie RCA Specialist',
  });
  const [profileSaved, setProfileSaved] = useState(false);

  // Change Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleTabChange = (tab: ProfileSubTab) => {
    setActiveSubTab(tab);
    onNavigate('user_profile', { subTab: tab });
  };

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2500);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword) {
      setPasswordError('Please enter your current password.');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError('New password cannot be the same as your current password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setIsSubmittingPassword(true);

    try {
      // Send real API request to backend
      const response = await fetch('/api/v1/users/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username || 'erdal.aydeniz',
          currentPassword,
          newPassword,
        }),
      }).catch(() => null);

      if (response && !response.ok) {
        const data = await response.json().catch(() => null);
        if (data?.error) {
          throw new Error(data.error);
        }
      }

      // Persist password update event in user session
      try {
        const storedAuth = localStorage.getItem('auth_user');
        if (storedAuth) {
          const parsed = JSON.parse(storedAuth);
          parsed.passwordUpdatedAt = new Date().toISOString();
          localStorage.setItem('auth_user', JSON.stringify(parsed));
        }
      } catch {}

      setPasswordSuccess('Password has been successfully updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to update password. Please check your credentials.');
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const getBreadcrumbTitle = () => {
    switch (activeSubTab) {
      case 'account_info':
        return 'Account Info';
      case 'system_settings':
        return 'System Settings';
      case 'change_password':
        return 'Change Password';
    }
  };

  const getPageTitle = () => {
    switch (activeSubTab) {
      case 'account_info':
        return 'Personal Account & Identity Details';
      case 'system_settings':
        return 'System Settings & Authorized Modules';
      case 'change_password':
        return 'Change Password';
    }
  };

  // Filter authorized catalog based on user's current role permissions
  const allowedCatalog = useMemo(() => {
    const role = userRole || 'USER';
    return AUTHORIZED_MODULES_CATALOG
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((item) => {
          if (item.targetModule) {
            return isModuleAllowedForRole(role, item.targetModule);
          }
          return true;
        }),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [userRole]);

  // Filter categories by query
  const filteredCategories = useMemo(() => {
    return allowedCatalog
      .map((cat) => {
        if (!searchQuery.trim()) return cat;
        const query = searchQuery.toLowerCase();
        const matchingItems = cat.items.filter((item) =>
          item.label.toLowerCase().includes(query)
        );
        if (matchingItems.length > 0 || cat.title.toLowerCase().includes(query)) {
          return {
            ...cat,
            items: matchingItems.length > 0 ? matchingItems : cat.items,
          };
        }
        return null;
      })
      .filter(Boolean) as AuthorizedPermissionCategory[];
  }, [allowedCatalog, searchQuery]);

  const totalPermissionsCount = useMemo(() => {
    return allowedCatalog.reduce((acc, cat) => acc + cat.items.length, 0);
  }, [allowedCatalog]);

  return (
    <div className="profile-page-container">
      {/* 1. Breadcrumbs */}
      <div className="profile-page-breadcrumbs">
        <button
          type="button"
          onClick={() => onNavigate('dashboard')}
          className="profile-crumb-link"
        >
          Dashboard
        </button>
        <span className="profile-crumb-separator">/</span>
        <span className="profile-crumb-text">User Profile</span>
        <span className="profile-crumb-separator">/</span>
        <span className="profile-crumb-current">{getBreadcrumbTitle()}</span>
      </div>

      {/* 2. Page Header with Title and Segmented Tabs */}
      <div className="profile-page-header">
        <h1 className="profile-page-title">{getPageTitle()}</h1>

        {/* Top Right Sub-Nav Pill Tabs matching reference screenshots */}
        <div className="profile-nav-tabs">
          <button
            type="button"
            className={`profile-nav-tab ${activeSubTab === 'account_info' ? 'active' : ''}`}
            onClick={() => handleTabChange('account_info')}
          >
            <User size={15} />
            <span>Account Info</span>
          </button>

          <button
            type="button"
            className={`profile-nav-tab ${activeSubTab === 'system_settings' ? 'active' : ''}`}
            onClick={() => handleTabChange('system_settings')}
          >
            <Settings size={15} />
            <span>System Settings</span>
          </button>

          <button
            type="button"
            className={`profile-nav-tab ${activeSubTab === 'change_password' ? 'active' : ''}`}
            onClick={() => handleTabChange('change_password')}
          >
            <Key size={15} />
            <span>Change Password</span>
          </button>
        </div>
      </div>

      {/* 3. Sub-Tab Content: SYSTEM SETTINGS & AUTHORIZED MODULES */}
      {activeSubTab === 'system_settings' && (
        <div className="profile-settings-card">
          {/* Header search & meta bar */}
          <div className="profile-settings-search-row">
            <div className="profile-search-input-wrap">
              <Search size={16} className="profile-search-icon" />
              <input
                type="text"
                placeholder="Search authorized modules, roles, or group scopes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="profile-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="profile-search-clear-btn"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="profile-authorized-count-badge">
              <Shield size={14} />
              <span>
                <strong>{totalPermissionsCount}</strong> Authorized Capabilities Assigned to <strong>{profileData.name}</strong>
              </span>
            </div>
          </div>

          {/* Categorized Permissions Grid */}
          <div className="profile-permissions-sections">
            {filteredCategories.length === 0 ? (
              <div className="profile-no-results">
                <AlertCircle size={24} color="#94a3b8" />
                <p>No authorized modules or capabilities matched "{searchQuery}"</p>
              </div>
            ) : (
              filteredCategories.map((category, idx) => (
                <div key={category.id} className="profile-permission-category-block">
                  <h3 className="profile-permission-category-title">
                    {category.title}
                  </h3>

                  <div className="profile-permission-pills-wrap">
                    {category.items.map((item) => (
                      <span
                        key={item.id}
                        className={`profile-permission-pill ${item.targetModule ? 'clickable' : ''}`}
                        onClick={() => {
                          if (item.targetModule) {
                            onNavigate(item.targetModule);
                          }
                        }}
                        title={
                          item.targetModule
                            ? `Authorized Module: Click to open ${item.label}`
                            : `Assigned Functional Scope: ${item.label}`
                        }
                      >
                        {item.label}
                        {item.targetModule && (
                          <ArrowRight size={11} className="profile-pill-arrow" />
                        )}
                      </span>
                    ))}
                  </div>

                  {idx < filteredCategories.length - 1 && (
                    <div className="profile-category-divider" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 4. Sub-Tab Content: ACCOUNT INFO */}
      {activeSubTab === 'account_info' && (
        <div className="profile-settings-card">
          <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* User Profile Header Card */}
            <div className="profile-card-user-banner">
              <div className="profile-banner-avatar">
                {profileData.name
                  .split(' ')
                  .map((p) => p[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </div>
              <div className="profile-banner-meta">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <h2 className="profile-banner-name">{profileData.name}</h2>
                  <span className="profile-active-badge">
                    <BadgeCheck size={14} /> Active Personnel
                  </span>
                </div>
                <div className="profile-banner-subtitle">
                  {profileData.position} • {profileData.department}
                </div>
                <div className="profile-banner-org">
                  Staff ID: <strong>{profileData.staffId}</strong> | Organization:{' '}
                  <strong>{organizationName} ({organizationCode})</strong>
                </div>
              </div>
            </div>

            {profileSaved && (
              <div className="profile-alert success">
                <CheckCircle2 size={16} />
                <span>Personal account profile details saved successfully.</span>
              </div>
            )}

            {/* Form Fields Grid */}
            <div className="profile-form-grid">
              <div className="profile-form-group">
                <label><Mail size={13} /> Official Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                />
              </div>

              <div className="profile-form-group">
                <label><Phone size={13} /> Mobile / Extension</label>
                <input
                  type="text"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                />
              </div>

              <div className="profile-form-group">
                <label><Briefcase size={13} /> Position / Title</label>
                <input
                  type="text"
                  value={profileData.position}
                  onChange={(e) => setProfileData({ ...profileData, position: e.target.value })}
                />
              </div>

              <div className="profile-form-group">
                <label><Key size={13} /> System Role / Permissions Scope</label>
                <input
                  type="text"
                  value={userRole || 'SAFETY_MANAGER'}
                  readOnly
                  style={{ background: '#f8fafc', color: '#334155', fontWeight: 600 }}
                />
              </div>

              <div className="profile-form-group">
                <label><Building size={13} /> Department</label>
                <input
                  type="text"
                  value={profileData.department}
                  onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                />
              </div>

              <div className="profile-form-group">
                <label><MapPin size={13} /> Base Station / Airport</label>
                <input
                  type="text"
                  value={profileData.station}
                  onChange={(e) => setProfileData({ ...profileData, station: e.target.value })}
                />
              </div>

              <div className="profile-form-group">
                <label><Shield size={13} /> Staff ID Code</label>
                <input
                  type="text"
                  value={profileData.staffId || 'EMP-2026-084'}
                  readOnly
                  style={{ background: '#f8fafc', color: '#334155', fontWeight: 600 }}
                />
              </div>

              <div className="profile-form-group">
                <label><Building size={13} /> Organization Scope</label>
                <input
                  type="text"
                  value={`${organizationName} (${organizationCode})`}
                  readOnly
                  style={{ background: '#f8fafc', color: '#334155', fontWeight: 600 }}
                />
              </div>

              <div className="profile-form-group">
                <label><Shield size={13} /> Direct Reporting Line</label>
                <input
                  type="text"
                  value={profileData.reportingManager}
                  onChange={(e) => setProfileData({ ...profileData, reportingManager: e.target.value })}
                />
              </div>
            </div>

            {/* Aviation Scope Box */}
            <div className="profile-aviation-scope-box">
              <div className="profile-scope-title">Aviation Qualifications & Scope</div>
              <div className="profile-scope-content">{profileData.certifications}</div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                className="submit-cancel-btn"
                onClick={() => onNavigate('dashboard')}
              >
                Back to Dashboard
              </button>
              <button type="submit" className="submit-confirm-btn">
                <Save size={15} />
                <span>Save Profile Changes</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 5. Sub-Tab Content: CHANGE PASSWORD (matching media_1792999416416_07451964.png) */}
      {activeSubTab === 'change_password' && (
        <div className="profile-settings-card change-password-card">
          <div className="change-password-header">
            <h3 className="change-password-title">Change Password</h3>
            <p className="change-password-subtitle">
              Update your password for @{username || 'erdal.aydeniz'}
            </p>
          </div>

          <div className="profile-card-divider" />

          {passwordSuccess && (
            <div className="profile-alert success" style={{ marginBottom: '20px' }}>
              <CheckCircle2 size={16} />
              <span>{passwordSuccess}</span>
            </div>
          )}

          {passwordError && (
            <div className="profile-alert error" style={{ marginBottom: '20px' }}>
              <AlertCircle size={16} />
              <span>{passwordError}</span>
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="change-password-form">
            <div className="profile-form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <div className="password-input-wrapper">
                <input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="profile-form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <span className="password-hint-text">
                Password must be at least 8 characters long.
              </span>
            </div>

            <div className="profile-form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="change-password-action-row">
              <button
                type="submit"
                className="btn-update-password"
                disabled={isSubmittingPassword}
              >
                {isSubmittingPassword ? (
                  <Loader2 size={15} className="spin-animate" />
                ) : (
                  <Key size={15} />
                )}
                <span>{isSubmittingPassword ? 'Updating Password...' : 'Update Password'}</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
